'use client'

import { useCallback, useMemo, useState } from 'react'
import { useAction, useMutation } from 'convex/react'
import { api } from '@convex/_generated/api'
import type { Id } from '@convex/_generated/dataModel'
import { uploadPhoto, Semaphore } from '@/lib/photos/upload-handler'
import { getSlotId } from '@/lib/photos/slots'
import { MAX_PHOTOS } from '@/lib/photos/constants'
import { type Photo, type UploadProgress, type PhotoUploadResult } from '@/lib/photos/types'

export interface UsePhotoUploadOptions {
  maxPhotos?: number
  maxConcurrent?: number
  onProgress?: (progress: UploadProgress) => void
  generateUploadUrl?: () => Promise<string>
  attestUpload?: (storageId: Id<'_storage'>) => Promise<unknown>
}

export type PhotoUploadOutcome =
  | { status: 'uploaded'; result: PhotoUploadResult }
  | { status: 'failed'; file: File; error: unknown }

export interface UsePhotoUploadReturn {
  upload: (
    files: File[],
    existingPhotos: Photo[],
    startIndex?: number
  ) => Promise<PhotoUploadResult[]>
  uploadEach: (
    files: File[],
    existingPhotos: Photo[],
    startIndex?: number
  ) => Promise<PhotoUploadOutcome[]>
  isUploading: boolean
  activeCount: number
}

function useImageAttestation(
  customAttestUpload?: (storageId: Id<'_storage'>) => Promise<unknown>
) {
  const attestProductImage = useAction(api.products.imageAssets.attestUpload)
  return useMemo(
    () => customAttestUpload ?? ((storageId: Id<'_storage'>) => attestProductImage({ storageId })),
    [attestProductImage, customAttestUpload]
  )
}

interface UploadPhotoInSlotArgs {
  file: File
  slotIndex: number
  semaphore: Semaphore
  generateUploadUrl: () => Promise<string>
  attestUpload: (storageId: Id<'_storage'>) => Promise<unknown>
  onProgress?: (progress: UploadProgress) => void
}

async function uploadPhotoInSlot({
  file,
  slotIndex,
  semaphore,
  generateUploadUrl,
  attestUpload,
  onProgress,
}: UploadPhotoInSlotArgs): Promise<PhotoUploadResult> {
  await semaphore.acquire()
  try {
    return await uploadPhoto({
      file,
      slot: getSlotId(slotIndex),
      generateUploadUrl,
      attestUpload,
      onProgress,
    })
  } finally {
    semaphore.release()
  }
}

type UploadBatchArgs = Omit<UploadPhotoInSlotArgs, 'file' | 'slotIndex'> & {
  files: File[]
  baseIndex: number
  onSettled: () => void
}

async function uploadBatch({ files, baseIndex, onSettled, ...slotArgs }: UploadBatchArgs): Promise<PhotoUploadOutcome[]> {
  return await Promise.all(
    files.map(async (file, index): Promise<PhotoUploadOutcome> => {
      try {
        const result = await uploadPhotoInSlot({ ...slotArgs, file, slotIndex: baseIndex + index })
        return { status: 'uploaded', result }
      } catch (error) {
        return { status: 'failed', file, error }
      } finally {
        onSettled()
      }
    })
  )
}

function uploadedOrThrow(outcomes: PhotoUploadOutcome[]): PhotoUploadResult[] {
  const results: PhotoUploadResult[] = []
  for (const outcome of outcomes) {
    if (outcome.status === 'failed') throw outcome.error
    results.push(outcome.result)
  }
  return results
}

export function usePhotoUpload(
  options: UsePhotoUploadOptions = {}
): UsePhotoUploadReturn {
  const { maxPhotos = MAX_PHOTOS, maxConcurrent = 3, onProgress, generateUploadUrl: customGenerateUploadUrl, attestUpload: customAttestUpload } = options
  const defaultGenerateUploadUrl = useMutation(api.files.mutations.generateUploadUrl)
  const attestUpload = useImageAttestation(customAttestUpload)
  const generateUploadUrl = useMemo(
    () => customGenerateUploadUrl ?? (async () => defaultGenerateUploadUrl({})),
    [customGenerateUploadUrl, defaultGenerateUploadUrl]
  )

  const [activeCount, setActiveCount] = useState(0)

  const uploadEach = useCallback(
    async (
      files: File[],
      existingPhotos: Photo[],
      startIndex?: number
    ): Promise<PhotoUploadOutcome[]> => {
      if (existingPhotos.length + files.length > maxPhotos) {
        throw new Error(`Maximum ${maxPhotos} photos allowed`)
      }
      setActiveCount((prev) => prev + files.length)
      return await uploadBatch({
        files,
        baseIndex: startIndex ?? existingPhotos.length,
        semaphore: new Semaphore(maxConcurrent),
        generateUploadUrl,
        attestUpload,
        onProgress,
        onSettled: () => setActiveCount((prev) => prev - 1),
      })
    },
    [attestUpload, generateUploadUrl, maxPhotos, maxConcurrent, onProgress]
  )

  const upload = useCallback(
    async (files: File[], existingPhotos: Photo[], startIndex?: number) =>
      uploadedOrThrow(await uploadEach(files, existingPhotos, startIndex)),
    [uploadEach]
  )

  return {
    upload,
    uploadEach,
    isUploading: activeCount > 0,
    activeCount,
  }
}
